import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    assignMetricTags,
    disManager,
    logout,
    metricTagsToHeaders,
    useApiClient,
    useApiClientModeSetter,
} from '@m43/core';
import { WindowLoadingBackground } from '../common/components/window-loading-background';
import { stableQueryKeys } from '../common/helpers/stable-query-keys';
import { BootstrapResource, CadNextBootstrapView, FieldNameEnum } from '@mark43/cad-api';
import { Flex, Spinner } from '@cad-arc';
import { initPendo } from '../monitoring/user-action-tracker/pendo';
import { useApplicationType } from '../common/hooks/use-application-type';
import { bootstrapDex } from './bootstrap-dex';
import { bootstrapUnitEmergencies } from './bootstrap-unit-emergencies';
import { electronBridge } from '../electron-bridge/electron-bridge';
import { colors } from '../colors/colors';
import { datadogRum, datadogLogs } from '@mark43/datadog';
import { setupDatadog } from '../monitoring/datadog';
import { setupOtel } from '../monitoring/otel-client';
import { logErrorMessage, setLoggingConfig } from '../monitoring/log';
import { setSocketUserId } from '../socket/create-socket';

export const BootstrapStateContext = React.createContext<CadNextBootstrapView | undefined>(
    undefined
);

export const BootstrapMessageContainer = ({ children }: { children: React.ReactNode }) => {
    return (
        <Flex
            bg={colors.bootstrapBackground}
            borderRadius="md"
            padding={8}
            alignItems="center"
            justifyContent="center"
        >
            {children}
        </Flex>
    );
};

const BootstrapNonThemeContainer = ({ children }: { children: React.ReactNode }) => {
    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '2rem',
                borderRadius: '1rem',
                fontSize: '2rem',
                fontWeight: 'bold',
                gap: '2rem',
                lineHeight: 1.5,
                textAlign: 'center',
                color: '#e1e1e1',
            }}
        >
            {children}
        </div>
    );
};

const SpinnerNonTheme = () => {
    return <Spinner style={{ height: '2rem', width: '2rem' }} />;
};

const ErrorContainerNonTheme = ({ children }: { children: React.ReactNode }) => {
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '2rem',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            {children}
        </div>
    );
};

const MessageTextNonTheme = ({ children }: { children: React.ReactNode }) => {
    return <div style={{ fontSize: '1rem' }}>{children}</div>;
};

const ButtonNonTheme = ({
    children,
    onClick,
}: {
    children: React.ReactNode;
    onClick?: () => void;
}) => {
    return (
        <button
            style={{
                padding: '0.5rem 1rem',
                borderRadius: '0.25rem',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 'bold',
                color: '#333333',
                backgroundColor: '#fefefe',
            }}
            onClick={onClick}
        >
            {children}
        </button>
    );
};

export const BootstrapLoadingContainer = () => {
    return (
        <WindowLoadingBackground>
            <BootstrapNonThemeContainer>
                <SpinnerNonTheme />
                <span>Loading...</span>
            </BootstrapNonThemeContainer>
        </WindowLoadingBackground>
    );
};

export const BootstrapErrorContainer = ({ onRetry }: { onRetry: () => void }) => {
    return (
        <WindowLoadingBackground>
            <BootstrapNonThemeContainer>
                <ErrorContainerNonTheme>
                    <div>
                        <div>Critical data failed to load.</div>
                        <MessageTextNonTheme>
                            Click &ldquo;Retry&rdquo; to reload the application. If the problem
                            persists contact your IT department.
                        </MessageTextNonTheme>
                    </div>

                    <ButtonNonTheme onClick={() => onRetry()}>Retry</ButtonNonTheme>
                </ErrorContainerNonTheme>
            </BootstrapNonThemeContainer>
        </WindowLoadingBackground>
    );
};

/**
 * Provider component that manages and provides bootstrap state to the application.
 * Fetches bootstrap data from the API and makes it available through context.
 *
 * NOTE: This provider is outside of the theming system, so here is the one place where we hardcode styles.
 *
 * @param props - Component props
 * @param props.children - Child components to render within the provider
 * @param props.override - Optional bootstrap view override for testing purposes
 * @returns React element that provides bootstrap state to its children
 */

export function BootstrapStateProvider(
    props: Readonly<{
        children: React.ReactElement;
        override?: CadNextBootstrapView;
    }>
): React.ReactElement {
    const apiClient = useApiClient();
    const editApiClient = useApiClientModeSetter();
    const {
        isCadElectron,
        isFirstResponderElectron,
        isFirstResponder,
        isElectron,
        isWeb,
    } = useApplicationType();
    const { data, isError, isInitialLoading, isFetching, refetch } = useQuery(
        stableQueryKeys.BOOTSTRAP_QUERY_KEY,
        () => {
            const isEventV2Electron =
                isElectron && electronBridge?.args['--workspace-window-type'] === 'EVENT_V2';
            const includeEventWindowData = isWeb || isEventV2Electron || isFirstResponderElectron;

            return apiClient
                .request<BootstrapResource.GetCadNextBootstrapView>({
                    method: 'POST',
                    url: 'bootstrap/cad_next',
                    data: {
                        fieldNames: [
                            FieldNameEnum.DISPLAY_CAD_UNIT_OFF_DUTY.name,
                            FieldNameEnum.AGENCY_EVENT_REPORTING_EVENT_NUMBER.name,
                            FieldNameEnum.DISPLAY_PRIMARY_SUBDIVISION.name,
                            FieldNameEnum.DISPLAY_ONLY_SUB_PREMISE_NAME_LABEL.name,
                            FieldNameEnum.DISPLAY_ONLY_SUB_PREMISE_VALUE_LABEL.name,
                        ],
                        windowId: isCadElectron
                            ? electronBridge?.args['--workspace-window-id']
                            : undefined,
                        includeEventPanelOptions: includeEventWindowData,
                        includeSubdivisionOptions: true,
                    },
                })
                .then(async (response) => {
                    const data = response.data.data;
                    if (data) {
                        // Set logging configuration based on feature flags
                        setLoggingConfig({
                            feLoggingEnabled: data.enabledFeatures.feLoggingEnabled,
                            feDebugLoggingEnabled: data.enabledFeatures.feDebugLoggingEnabled,
                            datadogEnabled: data.enabledFeatures.datadogEnabled,
                        });

                        if (isFirstResponder && !data.enabledFeatures.frNextEnabled) {
                            logout(apiClient);
                            return;
                        }
                        if (
                            isWeb &&
                            data.enabledFeatures.desktopIntegrationEnabled &&
                            data.disPort
                        ) {
                            disManager.start(data.disPort, data.userProfileView.userId);
                        }
                        assignMetricTags({
                            department_id: data.cadDepartmentAgencyView.departmentId.toString(),
                            department_subdomain: data.cadDepartmentAgencyView.subDomain,
                            user_id: data.userProfileView.userId.toString(),
                            user_email: data.userProfileView.primaryEmail,
                        });
                        setSocketUserId(data.userProfileView.userId);
                        if (data.enabledFeatures.pendoEnabled) {
                            initPendo(data);
                        }
                        if (data.enabledFeatures.datadogEnabled) {
                            try {
                                await setupDatadog({
                                    sessionReplayEnabled:
                                        data.enabledFeatures.datadogSessionReplayEnabled,
                                });
                                if (data.enabledFeatures.feCustomSpansEnabled) {
                                    await setupOtel({
                                        datadogApiKey: data.feDatadogApiKey,
                                    });
                                }
                                datadogRum.setUser({
                                    id: data.userProfileView.userId.toString(),
                                    email: data.userProfileView.primaryEmail,
                                });
                                datadogLogs.setUser({
                                    id: data.userProfileView.userId.toString(),
                                    email: data.userProfileView.primaryEmail,
                                });
                                if (data.taxonomy) {
                                    datadogRum.setGlobalContextProperty(
                                        'taxonomy',
                                        data.taxonomy
                                    );
                                    datadogLogs.setGlobalContextProperty(
                                        'taxonomy',
                                        data.taxonomy
                                    );
                                }
                            } catch (e) {
                                // no-op, datadog loading is not critical
                            }
                        }
                        if (!window.Cypress) {
                            editApiClient.addHeader({
                                'x-cad-subpremise-support-enabled': data.enabledFeatures.subpremiseSupport.toString(),
                                ...metricTagsToHeaders(),
                            });
                        }
                    }
                    return data;
                })
                .then((data) => {
                    if (data) {
                        return Promise.all([
                            bootstrapDex(data, apiClient),
                            bootstrapUnitEmergencies(data, apiClient),
                        ]).then(([dexData, unitEmergenciesData]) => ({
                            ...dexData,
                            unAckedInProgressUnitEmergencies:
                                unitEmergenciesData.unAckedInProgressUnitEmergencies,
                        }));
                    }
                    return data;
                })
                .catch((err) => {
                    if (electronBridge && 'logEvent' in electronBridge) {
                        electronBridge.logEvent?.('web-bootstrap-failure', {
                            statusCode: err.stat,
                        });
                    }
                    if (err?.response?.status === 400) {
                        logErrorMessage(
                            `Bootstrap call returned 400 with message: ${err.response?.data.error}`,
                            {
                                status: 400,
                                error: err.response?.data?.error,
                            }
                        );
                    } else if (err?.response?.status === 403) {
                        logout(apiClient);
                        return;
                    }
                    throw err;
                });
        },
        { refetchOnWindowFocus: false }
    );

    if (isInitialLoading || isFetching) {
        return <BootstrapLoadingContainer />;
    }

    if (isError || !data) {
        return <BootstrapErrorContainer onRetry={() => refetch()} />;
    }

    return (
        <BootstrapStateContext.Provider value={data}>
            {props.children}
        </BootstrapStateContext.Provider>
    );
}

// We omit enabledFeatures from this type so that consumers are forced to use useEnabledFeatures()
// which is the only reliable source for up to date values for enabled features.
export type UseBootstrapStateReturns = Omit<CadNextBootstrapView, 'enabledFeatures'>;

export function useBootstrapState(): UseBootstrapStateReturns {
    const bootstrapState = React.useContext(BootstrapStateContext);
    if (!bootstrapState) {
        throw new Error('Unexpectedly did not find bootstrapState on context.');
    }
    return bootstrapState;
}
